/**
 * <uhs-course-limit> — the training course card's "already in cart" state.
 *
 * Wraps Dawn's buy-buttons in snippets/uhs-course-purchase.liquid. A course is
 * one per order (snippets/uhs-order-limit.liquid), so once its variant is in
 * the cart the Add to Cart button is disabled and reads "Already in cart"
 * instead of adding a second copy for the cart to correct afterwards.
 *
 * State comes from Liquid on page load (data-in-cart), then from /cart.js after
 * every PUB_SUB_EVENTS.cartUpdate - so it flips straight after an add, and back
 * when the course is removed in the drawer, without a reload. It also re-checks
 * when the tab becomes visible again (cart changed in another tab) and when the
 * page is restored from the back/forward cache, where the Liquid state is
 * stale. A capture-phase submit listener is the backstop for anything that
 * still submits the form.
 *
 * The cart drawer's own limit (assets/uhs-cart-limit.js) stays in place for
 * every other way a course can reach the cart.
 */
if (!customElements.get('uhs-course-limit')) {
  customElements.define(
    'uhs-course-limit',
    class UhsCourseLimit extends HTMLElement {
      connectedCallback() {
        this.variantId = Number(this.dataset.variantId);
        this.button = this.querySelector('.product-form__submit');
        this.label = this.button?.querySelector('span');
        this.form = this.querySelector('form');
        if (!this.button || !this.label || !this.form) return;

        this.addLabel = this.label.textContent;
        this.onSubmit = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit, true);

        this.apply(this.dataset.inCart === 'true');

        this.onVisible = this.onVisible.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        document.addEventListener('visibilitychange', this.onVisible);
        window.addEventListener('pageshow', this.onPageShow);

        // PUB_SUB_EVENTS is a top-level `const` in assets/constants.js: a global
        // binding, but NOT a window property, so check it with typeof.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit, true);
        this.unsubscribe?.();
        document.removeEventListener('visibilitychange', this.onVisible);
        window.removeEventListener('pageshow', this.onPageShow);
      }

      onVisible() {
        if (document.visibilityState === 'visible') this.refresh();
      }

      /** @param {PageTransitionEvent} event */
      onPageShow(event) {
        if (event.persisted) this.refresh();
      }

      onSubmit(event) {
        if (!this.inCart) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      refresh() {
        fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => this.apply(cart.items.some((item) => item.variant_id === this.variantId)))
          .catch(() => {});
      }

      /** @param {boolean} inCart */
      apply(inCart) {
        this.inCart = inCart;
        this.label.textContent = inCart ? this.dataset.inCartLabel : this.addLabel;
        // A sold-out course arrives disabled from Dawn; never re-enable that.
        if (inCart) {
          this.button.setAttribute('disabled', 'disabled');
          this.button.setAttribute('aria-disabled', 'true');
        } else if (this.dataset.available === 'true') {
          this.button.removeAttribute('disabled');
          this.button.removeAttribute('aria-disabled');
        }
      }
    },
  );
}
